import { logError } from '../utils/errorLogger';
import { unwrapPaginatedResponse, buildPaginationParams } from '../utils/paginatedApi';
import BaseApi from './BaseApi';
class HotelApi extends BaseApi {
  constructor() {
    super('hotels'); // Use BaseApi constructor with hotels prefix
  }

  // Hotel CRUD
  async createHotel(data) {
    try {
      const response = await this.getApiInstance(true).post('/hotels', data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getAllHotels() {
    try {
      const response = await this.getApiInstance(true).get('/hotels');
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Search hotels by name (backend only responds when q has >= 3 chars)
  async searchHotelsByName(q, limit = 20) {
    try {
      const response = await this.getApiInstance(true).get('/hotels/search', {
        params: { q, limit },
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /** Select a single hotel by name (exact → prefix → contains). */
  async selectHotelByName(name) {
    try {
      const response = await this.getApiInstance(true).get('/hotels/by-name', {
        params: { name },
      });
      return response.data || null;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getHotelsPage({ page = 1, limit = 10 } = {}) {
    try {
      const response = await this.getApiInstance(true).get('/hotels', {
        params: buildPaginationParams({ page, limit }),
      });
      return unwrapPaginatedResponse(response.data);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async filterHotelsPage(filterData, { page = 1, limit = 10 } = {}) {
    try {
      const response = await this.getApiInstance(true).post('/hotels/filter', filterData, {
        params: buildPaginationParams({ page, limit }),
      });
      return unwrapPaginatedResponse(response.data);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getCitiesByCountry(paysName) {
    if (!paysName) return [];
    try {
      const response = await this.getApiInstance(true).get('/reservation-hotel/cities', {
        params: { pays_name: paysName },
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getCountries() {
    try {
      const response = await this.getApiInstance(true).get('/clients/pays');
      const rows = Array.isArray(response.data) ? response.data : [];
      return rows
        .map((pays) => pays?.name)
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b), 'fr', { sensitivity: 'base' }));
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getHotelById(id) {
    try {
      const response = await this.getApiInstance(true).get(`/hotels/${id}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async updateHotel(id, data) {
    try {
      const response = await this.getApiInstance(true).put(`/hotels/${id}`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async deleteHotel(id) {
    try {
      const response = await this.getApiInstance(true).delete(`/hotels/${id}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  async getGeneralDescription(id) {
    try {
      const response = await this.getApiInstance(true).get(`/hotels/${id}/general-description`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async updateGeneralDescription(id, data) {
    try {
      const response = await this.getApiInstance(true).put(
        `/hotels/${id}/general-description`,
        data
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getAllHotelsChambres() {
    try {
      const response = await this.getApiInstance(true).get('/hotelsChambres');
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async createHotelAeroport(id, data) {
    try {
      const response = await this.getApiInstance(true).post(`/hotels/${id}/aeroports`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getHotelAeroports(id) {
    try {
      const response = await this.getApiInstance(true).get(`/hotels/${id}/aeroports`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async updateHotelAeroport(aeroport_id, data) {
    try {
      const response = await this.getApiInstance(true).put(
        `/hotels/aeroports/${aeroport_id}`,
        data
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async deleteHotelAeroport(aeroport_id) {
    try {
      const response = await this.getApiInstance(true).delete(`/hotels/aeroports/${aeroport_id}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  async filterHotels(filterData) {
    try {
      const response = await this.getApiInstance(true).post('/hotels/filter', filterData);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getLocationId(hotelName, latitude, longitude) {
    try {
      const response = await this.getApiInstance(true).post('hotels/tripadvisor/location-id', {
        hotelName,
        latitude,
        longitude,
      });
      return response.data.locationId;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Fetch TripAdvisor Rating
  async getRating(locationId) {
    try {
      const response = await this.getApiInstance(true).post('hotels/tripadvisor/rating', {
        locationId,
      });
      return response.data.rating;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Contacts for a specific hotel
  async createContact(id_hotel, data) {
    try {
      const response = await this.getApiInstance(true).post(`/hotels/${id_hotel}/contacts`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getContactsByHotelId(id_hotel) {
    try {
      const response = await this.getApiInstance(true).get(`/hotels/${id_hotel}/contacts`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async updateContact(id, data) {
    try {
      const response = await this.getApiInstance(true).put(`/contacts/${id}`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async deleteContact(id) {
    try {
      const response = await this.getApiInstance(true).delete(`/contacts/${id}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Facilities for a specific hotel
  async createFacility(id_hotel, data) {
    try {
      const response = await this.getApiInstance(true).post(`/hotels/${id_hotel}/facilities`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getFacilitiesByHotelId(id_hotel) {
    try {
      const response = await this.getApiInstance(true).get(`/hotels/${id_hotel}/facilities`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async updateFacility(id, data) {
    try {
      const response = await this.getApiInstance(true).put(`/facilities/${id}`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async deleteFacility(id) {
    try {
      const response = await this.getApiInstance(true).delete(`/facilities/${id}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Media for a specific hotel
  async saveMedia(id, data) {
    try {
      const response = await this.getApiInstance(true).post(`/hotel/${id}/media`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  async getMedia(id) {
    try {
      const response = await this.getApiInstance(true).get(`/hotel/${id}/media`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  async deletemedia(url, hotelId, directory) {
    try {
      const response = await this.getApiInstance(true).delete(`/file/mediaFile`, {
        data: { url, hotelId, directory },
      });
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Equipment for a specific hotel
  async addEquipmentToHotel(id_hotel, data) {
    try {
      const response = await this.getApiInstance(true).post(`/hotels/${id_hotel}/equipment`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getEquipmentByHotelId(id_hotel) {
    try {
      const response = await this.getApiInstance(true).get(`/hotels/${id_hotel}/equipment`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async removeEquipmentFromHotel(id) {
    try {
      const response = await this.getApiInstance(true).delete(`/hotels/${id}/equipment`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async updateEquipmentHotel(id, data) {
    try {
      return await this.getApiInstance(true).put(`/hotels/${id}/equipment`, data);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Fetch all alerts for a specific hotel
  async getAlertsByHotelId(id_hotel) {
    try {
      const response = await this.getApiInstance(true).get(`/hotels/${id_hotel}/alerts`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Create a new alert for a specific hotel
  async createAlert(alertData) {
    try {
      const response = await this.getApiInstance(true).post(
        `/hotels/${alertData.id_hotel}/alerts`,
        alertData
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Update an alert by alert ID
  async updateAlert(id_alert, alertData) {
    try {
      const response = await this.getApiInstance(true).put(`/alerts/${id_alert}`, alertData);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Delete an alert by alert ID
  async deleteAlert(id_alert) {
    try {
      const response = await this.getApiInstance(true).delete(`/alerts/${id_alert}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Fetch all alert configurations
  async getAllAlertConfigurations() {
    try {
      const response = await this.getApiInstance(true).get(`/alert-configuration`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Add a chambre to a hotel
  async addChambreToHotel(id_hotel, chambreData) {
    try {
      const response = await this.getApiInstance(true).post(
        `/hotel/${id_hotel}/chambre`,
        chambreData
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Delete a chambre by its unique ID
  async deleteChambre(id) {
    try {
      const response = await this.getApiInstance(true).delete(`/hotel/chambres/${id}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Update a chambre by its unique ID
  async updateChambre(id, updatedData) {
    try {
      const response = await this.getApiInstance(true).put(`/hotel/chambres/${id}`, updatedData);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get all chambres for a specific hotel
  async getChambresByHotel(id_hotel) {
    try {
      const response = await this.getApiInstance(true).get(`/hotel/${id_hotel}/chambres`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get chambre hotel by id
  async getChambreHotelById(id) {
    try {
      const response = await this.getApiInstance(true).get(`/hotel/chambres/${id}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get all global chambres (not associated with hotels)
  async getAllChambres() {
    try {
      const response = await this.getApiInstance(true).get('/chambres');
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  /** Server-side chambre search for pickers (`q` min length handled by caller). */
  async searchChambres(q, { limit = 25 } = {}) {
    try {
      const response = await this.getApiInstance(true).get('/chambres/search', {
        params: { q, limit },
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getChambresPage({ page = 1, limit = 10 } = {}) {
    try {
      const response = await this.getApiInstance(true).get('/chambres', {
        params: buildPaginationParams({ page, limit }),
      });
      return unwrapPaginatedResponse(response.data);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Create a global chambre (not associated with any hotel)
  async createGlobalChambre(chambreData) {
    try {
      const response = await this.getApiInstance(true).post('/chambres', chambreData);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Hotel Proximite API methods
  async createProximite(id_hotel, data) {
    try {
      const response = await this.getApiInstance(true).post(`/hotels/${id_hotel}/proximite`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getProximiteByHotelId(id_hotel) {
    try {
      const response = await this.getApiInstance(true).get(`/hotels/${id_hotel}/proximite`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async updateProximite(id, data) {
    try {
      // Create a copy of data without timestamp fields
      const processedData = { ...data };
      delete processedData.utime;
      delete processedData.ctime;

      const response = await this.getApiInstance(true).put(
        `/hotels/proximite/${id}`,
        processedData
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async deleteProximite(id) {
    try {
      const response = await this.getApiInstance(true).delete(`/hotels/proximite/${id}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Hotel Details API methods
  async createDetails(id_hotel, data) {
    try {
      const response = await this.getApiInstance(true).post(`/hotels/${id_hotel}/details`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getDetailsByHotelId(id_hotel) {
    try {
      const response = await this.getApiInstance(true).get(`/hotels/${id_hotel}/details`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async updateDetails(id, data) {
    try {
      const response = await this.getApiInstance(true).put(`/hotels/details/${id}`, data);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async deleteDetails(id) {
    try {
      const response = await this.getApiInstance(true).delete(`/hotels/details/${id}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  async getDetailsCategories() {
    try {
      const response = await this.getApiInstance(true).get('/hotels/details/details-categories');
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  async getHotelStats() {
    try {
      const response = await this.getApiInstance(true).get('/hotels/stats');
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  async getRecentHotels() {
    try {
      const response = await this.getApiInstance(true).get('/hotels/recent');
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Verify rooms with all null attributes
  async verifyNotMappedRooms() {
    try {
      const response = await this.getApiInstance(true).get('/hotels/verify-not-mapped-rooms');
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Bulk update chambre attributes
  async bulkUpdateChambreAttributes(chambreIds, attributeType, attributeValue) {
    try {
      const response = await this.getApiInstance(true).post('/hotels/bulk-update-chambre-attributes', {
        chambreIds,
        attributeType,
        attributeValue
      });
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get hotels with missing location data
  async getUnmappedHotels() {
    try {
      const response = await this.getApiInstance(true).get('/hotels/unmapped-location');
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Bulk update hotel location data
  async bulkUpdateHotelLocation(updates) {
    try {
      const response = await this.getApiInstance(true).post('/hotels/bulk-update-location', {
        updates
      });
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }
}
export default new HotelApi();
